/* ============================================
   VisualReviewModal — Review AI-generated images
   Side-by-side: image + post details.
   Navigate between posts, approve/reject/regen.
   ============================================ */
import { useState } from 'react'
import type { SocialPost } from '@/lib/types'
import { IconX } from '@/components/icons/Icons'
import { STATUS_CONFIG } from '@/components/social-media/PostCard'
import { ChannelIcon, CHANNEL_NAMES } from '@/components/social-media/PostDetailModal'

interface VisualReviewModalProps {
  posts: SocialPost[]
  onClose: () => void
  onApprove: (postId: number) => void
  onApproveAll: () => void
  onReject: (postId: number) => void
  onRegenerate: (postId: number, prompt?: string) => void
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Sin fecha'
  return new Date(dateStr).toLocaleDateString('es-PE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Lima',
  })
}

export default function VisualReviewModal({
  posts,
  onClose,
  onApprove,
  onApproveAll,
  onReject,
  onRegenerate,
}: VisualReviewModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [editingPrompt, setEditingPrompt] = useState(false)
  const [promptValue, setPromptValue] = useState('')
  const [imageZoomed, setImageZoomed] = useState(false)

  const post = posts[currentIndex]
  if (!post) return null

  const statusCfg = STATUS_CONFIG[post.status] ?? STATUS_CONFIG.draft
  const imageUrl = post.media_urls?.[0] ?? null
  const total = posts.length
  const reviewed = posts.filter(p => p.status === 'approved').length

  const goNext = () => {
    if (currentIndex < total - 1) setCurrentIndex(i => i + 1)
    setEditingPrompt(false)
    setImageZoomed(false)
  }
  const goPrev = () => {
    if (currentIndex > 0) setCurrentIndex(i => i - 1)
    setEditingPrompt(false)
    setImageZoomed(false)
  }

  const handleEditPrompt = () => {
    setPromptValue(post.image_prompt ?? '')
    setEditingPrompt(true)
  }

  const handleRegenerate = () => {
    const newPrompt = editingPrompt ? promptValue : undefined
    onRegenerate(post.id, newPrompt)
    setEditingPrompt(false)
  }

  return (
    <div className="modal-overlay visual-review-overlay" onClick={onClose}>
      <div className="modal-content visual-review-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="visual-review-modal__header">
          <div className="visual-review-modal__title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
            <span>Revisión Visual</span>
            <span className="visual-review-modal__counter">
              {currentIndex + 1} / {total}
            </span>
          </div>
          <div className="visual-review-modal__header-actions">
            <span className="visual-review-modal__reviewed">
              {reviewed} aprobados de {total}
            </span>
            <button type="button" className="modal-close" onClick={onClose}>
              <IconX size={18} />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="visual-review-modal__progress">
          {posts.map((p, idx) => (
            <button
              key={p.id}
              type="button"
              className={`visual-review-modal__progress-dot ${
                idx === currentIndex ? 'visual-review-modal__progress-dot--active' : ''
              } ${p.status === 'approved' ? 'visual-review-modal__progress-dot--approved' : ''}`}
              onClick={() => { setCurrentIndex(idx); setEditingPrompt(false); setImageZoomed(false) }}
              title={`Post ${idx + 1} — ${STATUS_CONFIG[p.status]?.label ?? p.status}`}
            />
          ))}
        </div>

        {/* Main content: image + details */}
        <div className="visual-review-modal__body">
          {/* Left: Image */}
          <div className={`visual-review-modal__image-panel ${imageZoomed ? 'visual-review-modal__image-panel--zoomed' : ''}`}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Generated post image"
                className="visual-review-modal__image"
                onClick={() => setImageZoomed(!imageZoomed)}
              />
            ) : (
              <div className="visual-review-modal__no-image">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3 }}>
                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                </svg>
                <span>Sin imagen generada</span>
              </div>
            )}
            {/* Image status overlay */}
            <div className="visual-review-modal__image-status">
              <span className={`sm-badge ${statusCfg.className}`}>{statusCfg.label}</span>
            </div>
          </div>

          {/* Right: Post details */}
          <div className="visual-review-modal__details-panel">
            {/* Channels + date */}
            <div className="visual-review-modal__meta">
              <div className="visual-review-modal__channels">
                {(post.channels ?? []).map(ch => (
                  <div key={ch} className="visual-review-modal__channel">
                    <ChannelIcon channel={ch} />
                    <span>{CHANNEL_NAMES[ch] ?? ch}</span>
                  </div>
                ))}
              </div>
              <div className="visual-review-modal__date">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                {formatDate(post.publication_date)}
              </div>
            </div>

            {/* Caption */}
            <div className="visual-review-modal__caption-section">
              <label className="visual-review-modal__label">Caption</label>
              <div className="visual-review-modal__caption">
                {post.caption?.split('\n').map((line, i) => (
                  <span key={i}>{line}{i < (post.caption?.split('\n').length || 1) - 1 && <br />}</span>
                )) || '(Sin texto)'}
              </div>
            </div>

            {/* Hashtags */}
            {post.hashtags.length > 0 && (
              <div className="visual-review-modal__hashtags">
                {post.hashtags.map((tag, i) => (
                  <span key={i} className="visual-review-modal__tag">#{tag}</span>
                ))}
              </div>
            )}

            {/* Image prompt */}
            <div className="visual-review-modal__prompt-section">
              <label className="visual-review-modal__label">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                  <path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/>
                </svg>
                Prompt de imagen
                {!editingPrompt && (
                  <button type="button" className="visual-review-modal__edit-prompt-btn" onClick={handleEditPrompt} title="Editar prompt">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                    </svg>
                  </button>
                )}
              </label>
              {editingPrompt ? (
                <textarea
                  className="form-textarea visual-review-modal__prompt-editor"
                  value={promptValue}
                  onChange={e => setPromptValue(e.target.value)}
                  rows={4}
                  autoFocus
                />
              ) : (
                <div className="visual-review-modal__prompt-text">
                  {post.image_prompt ?? '(Sin prompt)'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="visual-review-modal__footer">
          {/* Navigation */}
          <div className="visual-review-modal__nav">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={goPrev}
              disabled={currentIndex === 0}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              Anterior
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={goNext}
              disabled={currentIndex === total - 1}
            >
              Siguiente
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>

          {/* Post actions */}
          <div className="visual-review-modal__actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => onReject(post.id)}
              style={{ color: 'var(--danger)' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              Rechazar
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleRegenerate}
              style={{ color: 'var(--warning)' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
              {editingPrompt ? 'Regenerar con nuevo prompt' : 'Regenerar'}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => { onApprove(post.id); if (currentIndex < total - 1) goNext() }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Aprobar Visual
            </button>
          </div>

          {/* Approve all */}
          {total > 1 && (
            <button
              type="button"
              className="btn btn-accent btn-sm visual-review-modal__approve-all"
              onClick={onApproveAll}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Aprobar todas ({total})
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
